import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  forwardRef,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { AutoUnsubscribe } from 'src/core/decorators/auto-unsubscribe.decorator';
import { CategoryService } from '../../services/category.service';
import { CategoryDto } from 'src/core/models/query/category.dto';
import {
  Subject,
  Subscription,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  take,
} from 'rxjs';
import { UserDefinedCategorySelectorModel } from '../../models/user-defined-category-selector.model';
import { QueryDto } from 'src/core/models/query/query.dto';
import { BaseDropdownSelectorComponent } from 'src/ui/base-selector/base-selector.module';
import { NgbPopover } from '@ng-bootstrap/ng-bootstrap';
import { QueryItemsCategoriesTupleDto } from 'src/core/models/query/query-items-categories-tuple.dto';
import { QueryItemDto } from 'src/core/models/query/query-item.dto';
import { CrudService } from 'src/core/services/crud/crud.service';
import { QueryService } from 'src/core/services/query/query.service';
import { ColorUtils } from 'src/core/helpers/color-utils';

@Component({
  selector: 'ca-user-defined-category-selector',
  templateUrl: './user-defined-category-selector.component.html',
  styleUrls: ['./user-defined-category-selector.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => UserDefinedCategorySelector),
    },
  ],
  standalone: false,
})
@AutoUnsubscribe()
export class UserDefinedCategorySelector
  extends BaseDropdownSelectorComponent<UserDefinedCategorySelectorModel>
  implements OnInit, AfterViewInit, OnDestroy, ControlValueAccessor
{
  @Input()
  set conversationType(val: number | null) {
    this._conversationType = val;

    this.conversationType$.next(val);
  }

  @Input()
  disableFn: (category: QueryDto) => boolean = item => false;

  @Input()
  headerInfo: string;

  @Input()
  headerInfoStyle: unknown;

  get conversationType(): number | null {
    return this._conversationType;
  }

  private conversationType$: Subject<number> = new Subject();
  private _conversationType: number | null = null;

  itemsPerPage: number = 10;

  isQueryPreviewLoading: boolean;
  previewQueryId: number;
  popover: NgbPopover;
  queryPreviewDataCache = new Map<number, QueryItemsCategoriesTupleDto>();
  queryPreviewDataSubscription: Subscription;

  constructor(
    private service: CategoryService,
    private crudService: CrudService,
    private queryService: QueryService,
    public colorUtils: ColorUtils,
    protected elementRef: ElementRef,
    protected ngZone: NgZone,
  ) {
    super(elementRef, ngZone);
  }

  ngOnInit(): void {
    super.ngOnInit();
  }

  ngAfterViewInit(): void {
    this.attachCategoryInput();
    this.attachConversationTypeId();
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
  }

  compareFn(a: CategoryDto, b: CategoryDto): boolean {
    return a.id === b.id;
  }

  checkOverflow(element) {
    return element.offsetHeight < element.scrollHeight || element.offsetWidth < element.scrollWidth;
  }

  toggleQueryPreviewPopover(popover: NgbPopover, query: QueryDto, event: any): boolean {
    this.previewQueryId = query.id;
    this.popover = popover;

    if (popover.isOpen()) {
      popover.close();
      this.previewQueryId = 0;
    } else {
      const data = this.queryPreviewDataCache.get(query.id);
      if (data == null) {
        this.isQueryPreviewLoading = true;
        const categoriesRequest = this.queryService.getQueryCategories();
        const queryItemsRequest = this.crudService.getById<QueryItemDto[]>(
          QueryDto,
          query.id,
          'items',
        );

        this.queryPreviewDataSubscription = combineLatest([
          categoriesRequest,
          queryItemsRequest,
        ]).subscribe(([categories, queryItems]) => {
          const previewData: QueryItemsCategoriesTupleDto = {
            queryBuilderCategoryData: categories.items,
            queryItems: queryItems,
          };

          this.queryPreviewDataCache.set(query.id, previewData);
          this.isQueryPreviewLoading = false;
          popover.open({ query: query, queryItems: queryItems, categories: categories.items });
        });
      } else {
        popover.open({
          query: query,
          queryItems: data.queryItems,
          categories: data.queryBuilderCategoryData,
        });
      }
    }

    event.stopPropagation();
    return false;
  }

  protected load(): void {
    this.loading = true;

    this.service
      .getUserDefinedCategories(
        this.conversationType,
        this.quickSearchTerm,
        this.filters,
        this.pageIndex,
        this.itemsPerPage,
      )
      .pipe(take(1))
      .subscribe(response => {
        this.totalCount = response.totalCount;
        this.items = response.items.map(x => {
          return { ...x, disabled: this.disableFn(x) } as UserDefinedCategorySelectorModel;
        });

        this.loading = false;
      });
  }

  private attachCategoryInput(): void {
    this.quickSearchTermInput$.pipe(debounceTime(250), distinctUntilChanged()).subscribe(val => {
      this.pageIndex = 0;
      this.quickSearchTerm = val;

      this.load();
    });
  }

  private attachConversationTypeId(): void {
    this.conversationType$.pipe(debounceTime(250), distinctUntilChanged()).subscribe(val => {
      this.pageIndex = 0;
      this.load();
    });
  }
}
